<template>
  <span class="ui-switch-field" :style="switchStyle">
    <span
      v-if="showStatus"
      class="ui-switch-status ui-switch-status--inactive"
      :class="{ 'is-current': !isActive, 'is-disabled': disabled }"
    >{{ $t(displayInactiveText) }}</span>
    <el-switch
      class="ui-switch"
      :value="value"
      :active-value="activeValue"
      :inactive-value="inactiveValue"
      :disabled="disabled"
      v-bind="$attrs"
      v-on="forwardedListeners"
      @input="handleInput"
      @change="handleChange"
    />
    <span
      v-if="showStatus"
      class="ui-switch-status ui-switch-status--active"
      :class="{ 'is-current': isActive, 'is-disabled': disabled }"
    >{{ $t(displayActiveText) }}</span>
  </span>
</template>

<script>
export default {
  name: 'UiSwitch',
  inheritAttrs: false,
  props: {
    value: {
      default: false
    },
    activeValue: {
      default: true
    },
    inactiveValue: {
      default: false
    },
    activeText: {
      type: String,
      default: '启用'
    },
    inactiveText: {
      type: String,
      default: '关闭'
    },
    activeStatusText: {
      type: String,
      default: ''
    },
    inactiveStatusText: {
      type: String,
      default: ''
    },
    showStatus: {
      type: Boolean,
      default: true
    },
    requiresSave: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    activeTone: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'warning', 'danger', 'accent', 'neutral'].includes(value)
    },
    inactiveTone: {
      type: String,
      default: 'warning',
      validator: value => ['primary', 'warning', 'danger', 'accent', 'neutral'].includes(value)
    }
  },
  data () {
    return {
      committedValue: this.value,
      userChanged: false
    }
  },
  computed: {
    isActive () {
      return this.value === this.activeValue
    },
    hasPendingValue () {
      return this.requiresSave && this.value !== this.committedValue
    },
    forwardedListeners () {
      const { input, change, ...listeners } = this.$listeners
      return listeners
    },
    resolvedActiveStatusText () {
      if (this.$locale === 'en') {
        const label = this.$t(this.activeText)
        return this.activeStatusText || ({ Show: 'Visible', Enable: 'Enabled', On: 'On' }[label] || label)
      }
      return this.activeStatusText || (this.activeText.startsWith('已') ? this.activeText : `已${this.activeText}`)
    },
    resolvedInactiveStatusText () {
      if (this.$locale === 'en') {
        const label = this.$t(this.inactiveText)
        return this.inactiveStatusText || ({ Hide: 'Hidden', Disable: 'Disabled', Close: 'Off' }[label] || label)
      }
      return this.inactiveStatusText || (this.inactiveText.startsWith('已') ? this.inactiveText : `已${this.inactiveText}`)
    },
    displayActiveText () {
      return this.isActive && !this.hasPendingValue ? this.resolvedActiveStatusText : this.activeText.replace(/^已/, '')
    },
    displayInactiveText () {
      return !this.isActive && !this.hasPendingValue ? this.resolvedInactiveStatusText : this.inactiveText.replace(/^已/, '')
    },
    switchStyle () {
      const toneMap = {
        primary: ['var(--ui-color-primary)', 'var(--ui-color-primary-rgb)'],
        warning: ['var(--ui-color-warning)', 'var(--ui-color-warning-rgb)'],
        danger: ['var(--ui-color-danger)', 'var(--ui-color-danger-rgb)'],
        accent: ['var(--ui-color-accent)', 'var(--ui-color-accent-rgb)'],
        neutral: ['var(--ui-text-secondary)', 'var(--ui-text-secondary-rgb)']
      }
      const active = toneMap[this.activeTone]
      const inactive = toneMap[this.inactiveTone]
      return {
        '--ui-switch-active-color': active[0],
        '--ui-switch-active-color-rgb': active[1],
        '--ui-switch-inactive-color': inactive[0],
        '--ui-switch-inactive-color-rgb': inactive[1]
      }
    }
  },
  watch: {
    value (value) {
      if (!this.requiresSave || !this.userChanged) this.committedValue = value
    }
  },
  mounted () {
    window.addEventListener('ui-form-commit-success', this.handleFormCommit)
  },
  beforeDestroy () {
    window.removeEventListener('ui-form-commit-success', this.handleFormCommit)
  },
  methods: {
    handleInput (value) {
      // Element UI 先触发 input、后触发 change。必须在把新值交给父组件前
      // 标记为待保存，否则 value 监听会把用户刚切换的值误判为后端已确认。
      if (this.requiresSave) this.userChanged = true
      this.$emit('input', value)
    },
    handleChange (value) {
      this.$emit('change', value)
    },
    handleFormCommit (event) {
      if (!this.requiresSave || !event.detail) return
      const scope = event.detail.scope
      if (scope && scope.contains(this.$el)) {
        this.committedValue = this.value
        this.userChanged = false
      }
    }
  }
}
</script>

<style lang="scss">
.ui-switch-field {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-space-sm);
  min-height: 24px;
  vertical-align: middle;
}

.ui-switch-status {
  min-width: 42px;
  font-size: var(--ui-font-size-caption);
  font-weight: var(--ui-font-weight-medium);
  line-height: 1;
  opacity: .42;
  white-space: nowrap;
  transition: color var(--ui-duration-fast) var(--ui-ease-standard),
    opacity var(--ui-duration-fast) var(--ui-ease-standard);

  &--inactive {
    color: var(--ui-switch-inactive-color);
    text-align: right;
  }

  &--active {
    color: var(--ui-switch-active-color);
  }

  &.is-current {
    opacity: 1;
    font-weight: var(--ui-font-weight-bold);
  }

  &.is-disabled {
    color: var(--ui-text-disabled);
    opacity: .7;
  }
}

.ui-switch {
  --ui-switch-active-color: var(--ui-color-primary);
  --ui-switch-active-color-rgb: var(--ui-color-primary-rgb);
  --ui-switch-inactive-color: var(--ui-color-warning);
  --ui-switch-inactive-color-rgb: var(--ui-color-warning-rgb);

  .el-switch__label {
    color: var(--ui-text-secondary);
    transition: color var(--ui-duration-fast) var(--ui-ease-standard);
  }

  .el-switch__label.is-active {
    color: var(--ui-switch-inactive-color);
  }

  &.is-checked {
    .el-switch__core {
      border-color: rgba(var(--ui-switch-active-color-rgb), .78) !important;
      background-color: rgba(var(--ui-switch-active-color-rgb), .38) !important;
    }

    .el-switch__label.is-active {
      color: var(--ui-switch-active-color);
    }
  }

  &:not(.is-checked) {
    .el-switch__core {
      border-color: rgba(var(--ui-switch-inactive-color-rgb), .78) !important;
      background-color: rgba(var(--ui-switch-inactive-color-rgb), .38) !important;
    }
  }

  &.is-disabled {
    .el-switch__core {
      border-color: var(--ui-border-color) !important;
      background-color: var(--ui-text-disabled) !important;
    }

    .el-switch__label,
    .el-switch__label.is-active {
      color: var(--ui-text-disabled);
    }
  }

  .el-switch__input:focus-visible + .el-switch__core {
    box-shadow: 0 0 0 3px rgba(var(--ui-color-primary-rgb), .16);
  }

  &:active:not(.is-disabled) .el-switch__core {
    transform: scale(.96);
  }
}
</style>
